import { ChannelTypeEnum } from 'lib-trend-core';
import { environment } from '../../../environments/environment';

/**
 * Facebook Login URLs for the Instagram and Messenger channels.
 *
 * Both the first connection (channel form) and the reconnect action (channel page) go through the
 * same authorization, so the scopes and the redirect paths live here only — a scope missing on one
 * of the two paths produces a channel that authorizes but cannot read messages.
 *
 * The redirect URIs are registered in the Meta app: changing them here requires changing them there.
 */
const GRAPH_VERSION = 'v23.0';

const INSTAGRAM_SCOPE = 'instagram_basic,pages_show_list,instagram_manage_messages,pages_manage_metadata,business_management,pages_messaging,pages_read_engagement';
const MESSENGER_SCOPE = 'pages_show_list,pages_manage_metadata,business_management,pages_messaging,pages_read_engagement';

export type MetaOAuthChannelType = ChannelTypeEnum.INSTAGRAM_API | ChannelTypeEnum.MESSENGER_API;

/**
 * Where Meta sends the browser back.
 *
 * Deployed environments use the configured app URL — the one registered in the Meta app. Locally it
 * follows the address the dev server is actually on, so `npm run start-app` (http) and
 * `npm run start-app-ssl` (https) each come back to themselves instead of to whichever protocol the
 * environment file happens to name. Both localhost URIs still have to be registered on Meta.
 */
function resolveAppOrigin(): string {
  return environment.production ? environment.trendWebAppUrl : window.location.origin;
}

/**
 * @param type the channel type being authorized
 * @param idChannel set when reconnecting an existing channel — travels in the OAuth `state` and
 *                  comes back in the redirect fragment, telling the redirect page which channel to
 *                  refresh instead of connecting every page the user administers
 */
export function buildMetaOAuthUrl(type: MetaOAuthChannelType, idChannel?: string): string {
  const isInstagram = type === ChannelTypeEnum.INSTAGRAM_API;
  const redirectPath = isInstagram ? '/channel/redirectInstagram/' : '/channel/redirectMessenger/';
  const scope = isInstagram ? INSTAGRAM_SCOPE : MESSENGER_SCOPE;
  const state = !!idChannel ? `&state=${idChannel}` : '';
  return `https://www.facebook.com/${GRAPH_VERSION}/dialog/oauth?client_id=${environment.cloudAPI.appId}&display=page&redirect_uri=${resolveAppOrigin()}${redirectPath}&response_type=token&scope=${scope}${state}`;
}
